import { CrewCustomerApi } from 'corso-types';
import pRetry, { AbortError } from 'p-retry';
import { generatePath } from 'react-router';
import env from '~/env';
import { NetworkError, ResponseError } from './errors';
import { addBreadcrumb } from './observability';
import { AsyncReturnType, Simplify } from './types';

const baseUrl = env.VITE_CREW_API_URL;
export type ApiPath = CrewCustomerApi.ApiPath;

type RequestConfig<
  Path extends CrewCustomerApi.ApiPath,
  Method extends CrewCustomerApi.MethodOfPath<Path>,
> = {
  path: Path;
  method: Method;
} & (CrewCustomerApi.RequestParams<Path, Method> extends never
  ? { params?: never }
  : { params: CrewCustomerApi.RequestParams<Path, Method> }) &
  (CrewCustomerApi.RequestBody<Path, Method> extends never
    ? { body?: never }
    : { body: CrewCustomerApi.RequestBody<Path, Method> }) &
  (CrewCustomerApi.RequestQuery<Path, Method> extends never
    ? { query?: never }
    : { query: CrewCustomerApi.RequestQuery<Path, Method> });

type RequestCreate<
  Path extends CrewCustomerApi.ApiPath,
  Method extends CrewCustomerApi.MethodOfPath<Path>,
> = Simplify<Pick<RequestConfig<Path, Method>, 'path' | 'method'>>;

type RequestParams<
  Path extends CrewCustomerApi.ApiPath,
  Method extends CrewCustomerApi.MethodOfPath<Path>,
> = Simplify<Pick<RequestConfig<Path, Method>, 'params' | 'query' | 'body'>>;

const abortOnSyntaxError = (resp: Response) => (cause: unknown) => {
  const error =
    cause instanceof SyntaxError
      ? new AbortError(new ResponseError({ response: resp, cause }))
      : cause;

  return Promise.reject(error);
};

export const parseResponse = <Data>(resp: Response) => {
  if (resp.status === 204) {
    return Promise.resolve(null) as Promise<Data>; // ! Assuming API contracts for Data to be nullable when 204
  }

  // some api responses (400s) are not json which causes a syntax errors
  const content = resp.headers.get('Content-Type')?.includes('application/json')
    ? resp.json()
    : resp.text();

  return content
    .then((body: unknown) => {
      if (resp.ok) return body as Data;

      throw new AbortError(new ResponseError({ response: resp, body }));
    })
    .catch(abortOnSyntaxError(resp));
};

/**
 * ! Bringing it back due to another issue in  `isNetworkError` check.
 * ! Going to use this to bypass p-retry's isNetworkError check.
 * ! this is ok with current implementation of request as the only case would be a network error,
 * ! but if we start using fetchInit, we need to revisit this
 */
const bypassPRetryIsNetworkError =
  (method: string, path: string) => (cause: unknown) => {
    const error =
      cause instanceof TypeError
        ? new NetworkError('Connection lost, refresh the page to try again', {
            cause: new Error(`Request Failure: ${method} | ${path}`, { cause }),
          })
        : cause;

    return Promise.reject(error);
  };

const compileUrl = ({
  path,
  params,
  query,
}: {
  path: string;
  params: Record<string, unknown> | undefined;
  query: Record<string, unknown> | undefined;
}) => {
  const url = new URL(`${baseUrl}${generatePath(path, params)}`);

  Object.entries(query ?? {})
    .filter(([, value]) => value !== null && value !== undefined)
    // ! this coercion is not safe, but query params should must always be interchanged as string values
    .map(([key, value]) => [key, String(value).trim()] as const)
    .forEach(([key, value]) => url.searchParams.append(key, value));

  return url.toString();
};

// strip query string — presigned upload URLs embed credentials/signatures there,
// and other request URLs may carry sensitive filters
const redactUrl = (raw: string) => {
  try {
    const parsed = new URL(raw);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return '<invalid url>';
  }
};

const buildRetryOptions = ({ url }: { url: string }) =>
  ({
    retries: 3,
    maxTimeout: 5000,
    randomize: true,
    maxRetryTime: 10000,
    onFailedAttempt: ({
      error,
      attemptNumber,
      retriesLeft,
      retriesConsumed,
      retryDelay,
    }) => {
      addBreadcrumb({
        level: 'warning',
        category: 'fetch.retry',
        message:
          retriesLeft > 0
            ? `Retrying fetch (attempt ${attemptNumber}, ${retriesLeft} left)`
            : `Fetch failed; no retries left (attempt ${attemptNumber})`,
        data: {
          url: redactUrl(url),
          errorName: error.name,
          errorMessage: error.message,
          attemptNumber,
          retriesLeft,
          retriesConsumed,
          retryDelay,
        },
      });
    },
    // ! needs the 'Network error' override in the fetch catch
    // * any error coming from 'parseResponse' should be an AbortError so then from the fetch catch will be an Error 'Network error'
    shouldRetry: (error) => !(error instanceof AbortError),
  }) satisfies Parameters<typeof pRetry>[1];

export const request =
  <
    Path extends CrewCustomerApi.ApiPath,
    Method extends CrewCustomerApi.MethodOfPath<Path>,
  >({
    path,
    method,
  }: RequestCreate<Path, Method>) =>
  (
    { params, query, body }: RequestParams<Path, Method>,
    fetchInit?: Omit<RequestInit, 'method' | 'body'>,
  ) => {
    const { headers: headersInit, ...init } = fetchInit ?? {};

    const headers = {
      // uses standard headers to normalize casing to allow overrides
      ...Object.fromEntries(
        new Headers({
          'Content-Type': 'application/json',
          Accept: 'application/json',
        }),
      ), // default headers
      ...Object.fromEntries(new Headers(headersInit)),
    };

    const requestInit = {
      method,
      headers,
      ...init,
      ...(body && { body: JSON.stringify(body) }),
    };

    const url = compileUrl({ path, params, query });

    return pRetry(
      () =>
        fetch(url, requestInit)
          .then(parseResponse<CrewCustomerApi.ResponseBody<Path, Method>>)
          .catch(bypassPRetryIsNetworkError(method, path)),
      buildRetryOptions({ url }),
    );
  };

const fetchSignedUrl = request({
  path: '/:storefrontId/signed-url',
  method: 'get',
});

// Single PUT against `uploadUrl`. Resolves to `{ name, src: assetUrl }` on
// 2xx, rejects with AbortError on non-2xx (so pRetry stops), or NetworkError
// on fetch-throw (so pRetry retries). `assetUrl` is the public CDN URL the
// caller passes through — it's not derivable from the upload URL because the
// presigned PUT URL carries credentials and a different host.
const cdnPut = ({
  uploadFilename,
  uploadUrl,
  assetUrl,
  file,
}: {
  uploadFilename: string;
  uploadUrl: string;
  assetUrl: string;
  file: File;
}) =>
  fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    // Presigned URLs must have this header set when using AWS S3 client on DigitalOcean @see https://stackoverflow.com/questions/66555915/cannot-upload-files-with-acl-public-read-to-digital-ocean-spaces
    headers: { 'x-amz-acl': 'public-read' },
  })
    .then((resp) => {
      if (resp.ok) return { name: uploadFilename, src: assetUrl };
      addBreadcrumb({
        level: 'error',
        type: 'error',
        message: `CDN error uploading ${uploadFilename}`,
        data: {
          status: resp.status,
          statusText: resp.statusText,
          uploadFilename,
          file: { name: file.name, type: file.type, size: file.size },
        },
      });
      throw new AbortError(
        new Error(`CDN error uploading ${uploadFilename}: ${resp.statusText}`),
      );
    })
    .catch(bypassPRetryIsNetworkError('PUT', redactUrl(uploadUrl)));

const uploadToCDN =
  (file: File) => (signedUrlResp: AsyncReturnType<typeof fetchSignedUrl>) =>
    pRetry(
      () =>
        cdnPut({
          uploadFilename: signedUrlResp.uploadFilename,
          uploadUrl: signedUrlResp.signedUploadUrl,
          assetUrl: signedUrlResp.url,
          file,
        }),
      buildRetryOptions({ url: signedUrlResp.signedUploadUrl }),
    ).catch((err: unknown) => {
      const proxyUrl = signedUrlResp.signedUploadProxyUrl;
      const directError = err instanceof Error ? err : undefined;

      addBreadcrumb({
        level: 'info',
        category: 'fetch.fallback',
        message: `Direct CDN PUT failed; retrying via proxy URL for ${signedUrlResp.uploadFilename}`,
        data: {
          uploadFilename: signedUrlResp.uploadFilename,
          directUrl: redactUrl(signedUrlResp.signedUploadUrl),
          proxyUrl: redactUrl(proxyUrl),
          directErrorName: directError?.name,
          directErrorMessage: directError?.message,
        },
      });

      // Lighter retry budget on the fallback — the direct path already
      // exhausted its budget, and the proxy host either resolves or it doesn't.
      return pRetry(
        () =>
          cdnPut({
            uploadFilename: signedUrlResp.uploadFilename,
            uploadUrl: proxyUrl,
            assetUrl: signedUrlResp.url,
            file,
          }).then((result) => {
            addBreadcrumb({
              level: 'info',
              category: 'fetch.fallback',
              message: `Uploaded to CDN via proxy URL after direct PUT failure for ${signedUrlResp.uploadFilename}`,
            });

            return result;
          }),
        { ...buildRetryOptions({ url: proxyUrl }), retries: 2 },
      );
    });

const uploadFile = ({
  params,
  query,
  body,
}: {
  params: Parameters<typeof fetchSignedUrl>[0]['params'];
  query: Simplify<
    Pick<Parameters<typeof fetchSignedUrl>[0]['query'], 'destination'>
  >;
  body: File;
}) =>
  fetchSignedUrl({
    params,
    query: {
      ...query,
      filename: body.name,
    },
  }).then(uploadToCDN(body));

export default {
  fetchSettings: request({
    path: '/settings/:slug',
    method: 'get',
  }),

  fetchRegistrationProducts: request({
    path: '/:storefrontId/registration/product',
    method: 'get',
  }),

  fetchCrewOrder: request({
    path: '/:storefrontId/order',
    method: 'get',
  }),

  withdrawCrewOrder: request({
    path: '/:storefrontId/order/withdraw',
    method: 'post',
  }),

  fetchProductVariantInfo: request({
    path: '/:storefrontId/product/exchange-options/:idFromPlatform',
    method: 'get',
  }),

  fetchCrewClaim: request({
    path: '/:storefrontId/claim/:claimExternalId',
    method: 'get',
  }),

  createCrewClaim: request({
    path: '/:storefrontId/claim',
    method: 'post',
  }),

  createRegistration: request({
    path: '/:storefrontId/registration',
    method: 'post',
  }),

  analyzeRegistrationReceipt: request({
    path: '/:storefrontId/registration/analyze',
    method: 'post',
  }),

  analyzeRegistrationReceiptProduct: request({
    path: '/:storefrontId/registration/analyze/product',
    method: 'post',
  }),

  createShopNowSession: request({
    path: '/:storefrontId/shop-now',
    method: 'post',
  }),

  fetchShopNowSession: request({
    path: '/:storefrontId/shop-now/:shopNowId',
    method: 'get',
  }),

  startOrderEdit: request({
    path: '/:storefrontId/order/:storeOrderId/edit',
    method: 'get',
  }),

  stageOrderEdit: request({
    path: '/:storefrontId/order/:storeOrderId/edit/stage',
    method: 'post',
  }),

  commitOrderEdit: request({
    path: '/:storefrontId/order/:storeOrderId/edit/commit',
    method: 'post',
  }),

  prepareOrderEdit: request({
    path: '/:storefrontId/order/:storeOrderId/edit/prepare',
    method: 'post',
  }),

  createShipmentMethodsQuote: request({
    path: '/:storefrontId/return-shipment/quote',
    method: 'post',
  }),

  fetchProtectionOrder: request({
    path: '/protection/order',
    method: 'get',
  }),

  createProtectionClaim: request({
    path: '/:storefrontId/shipping-protection/claim',
    method: 'post',
  }),

  uploadFile,

  // * business hooks

  calculateAfterReasonSelection: request({
    method: 'post',
    path: '/:storefrontId/order/calculate/after-reason-selection',
  }),

  confirmAfterReasonSelection: request({
    path: '/:storefrontId/automations/after-reason-selection',
    method: 'post',
  }),
  calculateClaimLineItemReview: request({
    path: '/:storefrontId/order/calculate/claim-line-item-review',
    method: 'post',
  }),

  calculateBeforeClaimSubmission: request({
    path: '/:storefrontId/order/calculate/before-claim-submission',
    method: 'post',
  }),
};
